<script setup lang="ts">
import {
  Plus,
  RefreshRight,
  Search,
} from '@element-plus/icons-vue'
import { ElButton, ElEmpty, ElIcon } from 'element-plus'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import awaitingInputImage from '@/assets/illustrations/empty-states/empty-07-awaiting-input-refined.webp'
import firstCreateImage from '@/assets/illustrations/empty-states/empty-01-first-create-refined.webp'
import loadErrorImage from '@/assets/illustrations/empty-states/empty-09-load-error-refined.webp'
import loadingImage from '@/assets/illustrations/empty-states/empty-08-loading-refined.webp'
import noChartDataImage from '@/assets/illustrations/empty-states/empty-05-no-chart-data-refined.webp'
import noMessagesImage from '@/assets/illustrations/empty-states/empty-11-no-messages-refined.webp'
import noPermissionImage from '@/assets/illustrations/empty-states/empty-10-no-permission-refined.webp'
import noRecordsImage from '@/assets/illustrations/empty-states/empty-03-no-records-refined.webp'
import noRelationsImage from '@/assets/illustrations/empty-states/empty-04-no-relations-refined.webp'
import noResourcesImage from '@/assets/illustrations/empty-states/empty-06-no-resources-refined.webp'
import noResultsImage from '@/assets/illustrations/empty-states/empty-02-no-results-refined.webp'

export type EmptyStateType =
  | 'first-create'
  | 'no-results'
  | 'no-records'
  | 'no-relations'
  | 'no-chart-data'
  | 'no-resources'
  | 'awaiting-input'
  | 'loading'
  | 'load-error'
  | 'no-permission'
  | 'no-messages'

interface Props {
  type?: EmptyStateType
  loading?: boolean
  error?: boolean
  filters?: Record<string, unknown> | null
  detectFilters?: boolean
  title?: string
  description?: string
  actionText?: string
  showAction?: boolean
  compact?: boolean
  micro?: boolean
  imageSize?: number
  createHandler?: (() => void) | null
  resetHandler?: (() => void) | null
  retryHandler?: (() => void) | null
  queryHandler?: (() => void) | null
}

const props = withDefaults(defineProps<Props>(), {
  type: 'no-records',
  loading: false,
  error: false,
  filters: null,
  detectFilters: true,
  title: '',
  description: '',
  actionText: '',
  showAction: true,
  compact: false,
  micro: false,
  imageSize: 0,
  createHandler: null,
  resetHandler: null,
  retryHandler: null,
  queryHandler: null,
})

const emit = defineEmits<{
  action: []
}>()

const { t } = useI18n()

const imageMap: Record<EmptyStateType, string> = {
  'first-create': firstCreateImage,
  'no-results': noResultsImage,
  'no-records': noRecordsImage,
  'no-relations': noRelationsImage,
  'no-chart-data': noChartDataImage,
  'no-resources': noResourcesImage,
  'awaiting-input': awaitingInputImage,
  'loading': loadingImage,
  'load-error': loadErrorImage,
  'no-permission': noPermissionImage,
  'no-messages': noMessagesImage,
}

const i18nKeyMap: Record<EmptyStateType, string> = {
  'first-create': 'firstCreate',
  'no-results': 'noResults',
  'no-records': 'noRecords',
  'no-relations': 'noRelations',
  'no-chart-data': 'noChartData',
  'no-resources': 'noResources',
  'awaiting-input': 'awaitingInput',
  'loading': 'loading',
  'load-error': 'loadError',
  'no-permission': 'noPermission',
  'no-messages': 'noMessages',
}

const ignoredFilterKeys = new Set([
  'page',
  'pageIndex',
  'pageNo',
  'pageNum',
  'pageNumber',
  'pageSize',
  'current',
  'currentPage',
  'size',
  'limit',
  'offset',
  'sort',
  'sortBy',
  'sortField',
  'sortOrder',
  'order',
  'orderBy',
])

function isMeaningfulValue(value: unknown, key = ''): boolean {
  if (ignoredFilterKeys.has(key))
    return false
  if (value === null || value === undefined || value === '')
    return false
  if (typeof value === 'boolean')
    return value
  if (typeof value === 'number')
    return value !== 0
  if (Array.isArray(value))
    return value.some(item => isMeaningfulValue(item))
  if (value instanceof Date)
    return true
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .some(([childKey, childValue]) => isMeaningfulValue(childValue, childKey))
  }
  return true
}

const hasActiveFilters = computed(() => {
  if (!props.detectFilters || !props.filters)
    return false
  return Object.entries(props.filters)
    .some(([key, value]) => isMeaningfulValue(value, key))
})

const effectiveType = computed<EmptyStateType>(() => {
  if (props.loading)
    return 'loading'
  if (props.error)
    return 'load-error'
  if (props.type !== 'awaiting-input' && props.detectFilters && hasActiveFilters.value)
    return 'no-results'
  return props.type
})

const textKey = computed(() => i18nKeyMap[effectiveType.value])
const resolvedTitle = computed(() => props.title || t(`emptyState.${textKey.value}.title`))

const defaultActionKey = computed(() => {
  switch (effectiveType.value) {
    case 'first-create':
      return 'emptyState.actions.create'
    case 'no-results':
      return 'emptyState.actions.reset'
    case 'awaiting-input':
      return 'emptyState.actions.query'
    case 'load-error':
      return 'emptyState.actions.retry'
    default:
      return ''
  }
})

const resolvedActionText = computed(() => {
  if (props.actionText)
    return props.actionText
  return defaultActionKey.value ? t(defaultActionKey.value) : ''
})

const actionIcon = computed(() => {
  switch (effectiveType.value) {
    case 'first-create':
      return Plus
    case 'no-results':
    case 'awaiting-input':
      return Search
    case 'load-error':
      return RefreshRight
    default:
      return null
  }
})

const hasActionHandler = computed(() => {
  if (props.actionText)
    return true
  switch (effectiveType.value) {
    case 'first-create':
      return Boolean(props.createHandler)
    case 'no-results':
      return Boolean(props.resetHandler)
    case 'awaiting-input':
      return Boolean(props.queryHandler)
    case 'load-error':
      return Boolean(props.retryHandler)
    default:
      return false
  }
})

const canShowAction = computed(() => {
  // 筛选无结果只显示插画和“未找到匹配结果”，不显示说明和重置按钮。
  if (effectiveType.value === 'no-results')
    return false
  return props.showAction && hasActionHandler.value && Boolean(resolvedActionText.value)
})

const resolvedImageSize = computed(() => {
  if (props.imageSize > 0)
    return props.imageSize
  return props.micro ? 72 : props.compact ? 108 : 144
})

const resolvedImageStyle = computed<Record<string, string>>(() => ({
  width: `${resolvedImageSize.value}px`,
  height: `${resolvedImageSize.value}px`,
}))

function handleAction() {
  switch (effectiveType.value) {
    case 'first-create':
      props.createHandler?.()
      break
    case 'no-results':
      props.resetHandler?.()
      break
    case 'awaiting-input':
      props.queryHandler?.()
      break
    case 'load-error':
      props.retryHandler?.()
      break
  }
  emit('action')
}
</script>

<template>
  <div
    class="business-empty-state"
    :class="[
      `is-${effectiveType}`,
      {
        'is-compact': compact,
        'is-micro': micro,
      },
    ]"
    role="status"
    :aria-live="effectiveType === 'load-error' ? 'assertive' : 'polite'"
  >
    <ElEmpty>
      <template #image>
        <div class="business-empty-state__image" :style="resolvedImageStyle">
          <img :src="imageMap[effectiveType]" alt="" aria-hidden="true">
        </div>
      </template>

      <template #description>
        <div class="business-empty-state__copy">
          <strong class="business-empty-state__title">{{ resolvedTitle }}</strong>
        </div>
      </template>

      <slot name="action">
        <ElButton
          v-if="canShowAction"
          :type="effectiveType === 'first-create' ? 'primary' : 'default'"
          size="default"
          @click="handleAction"
        >
          <template v-if="actionIcon" #icon>
            <ElIcon><component :is="actionIcon" /></ElIcon>
          </template>
          {{ resolvedActionText }}
        </ElButton>
      </slot>
    </ElEmpty>
  </div>
</template>

<style scoped>
.business-empty-state {
  box-sizing: border-box;
  display: grid;
  flex: 1 1 auto;
  grid-column: 1 / -1;
  place-items: center;
  place-self: stretch stretch;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  height: 100%;
  min-height: 180px;
  padding: 14px;
  margin: 0;
  overflow: hidden;
  line-height: normal;
  background: transparent;
}

.business-empty-state.is-compact {
  min-height: 120px;
  padding: 10px;
}

.business-empty-state.is-micro {
  min-height: 84px;
  padding: 4px;
}

.business-empty-state :deep(.el-empty) {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 0;
  padding: 0;
  margin: 0;
  overflow: hidden;
}

.business-empty-state :deep(.el-empty__image) {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: 100%;
  margin: 0 0 14px;
}

.business-empty-state.is-compact :deep(.el-empty__image) {
  margin-bottom: 10px;
}

.business-empty-state.is-micro :deep(.el-empty__image) {
  margin-bottom: 6px;
}

.business-empty-state__image {
  box-sizing: border-box;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
}

.business-empty-state__image img {
  display: block;
  width: 100%;
  max-width: 100%;
  height: 100%;
  max-height: 100%;
  object-fit: contain;
  object-position: center;
}

.business-empty-state :deep(.el-empty__description) {
  box-sizing: border-box;
  width: 100%;
  padding: 0 4px 2px;
  margin-top: 0;
  overflow: visible;
  line-height: normal;
}

.business-empty-state :deep(.el-empty__bottom) {
  margin-top: 18px;
}

.business-empty-state__copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
}

.business-empty-state__title {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #64748b;
  text-align: center;
  white-space: normal;
}

.business-empty-state.is-micro .business-empty-state__title {
  font-size: 12px;
  line-height: 18px;
}

</style>
